import { Product, ProductCustomTag, ProductVariantGroup } from '../types';
import { normalizeCategoryFolder, sanitizeProductImagePath } from './categoryFolders';
import { normalizeImageToWebp } from './imageToWebp';

export interface SmartMergeResult {
  mergedProducts: Product[];
  addedCount: number;
  updatedCount: number;
  unchangedCount: number;
  details: {
    addedTitles: string[];
    updatedTitles: string[];
  };
}

/**
 * Normalizes title for fuzzy comparison
 */
function normalizeTitle(title?: string): string {
  if (!title) return '';
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ');
}

/**
 * Deep, intelligent field-level merge for a single product
 */
function mergeSingleProduct(existing: Product, incoming: Partial<Product>): { merged: Product; isChanged: boolean } {
  let isChanged = false;
  const merged: Product = { ...existing };

  // 1. Title & Slug
  if (incoming.title && incoming.title.trim() && incoming.title !== existing.title) {
    merged.title = incoming.title.trim();
    isChanged = true;
  }
  if (incoming.slug && incoming.slug.trim() && incoming.slug !== existing.slug) {
    merged.slug = incoming.slug.trim();
    isChanged = true;
  }

  // 2. Category (strictly normalized)
  const incomingCategory = incoming.category ? normalizeCategoryFolder(incoming.category) : existing.category;
  if (incomingCategory !== existing.category) {
    merged.category = incomingCategory as any;
    isChanged = true;
  }

  // 3. Price & Compare Price
  if (typeof incoming.price === 'number' && incoming.price > 0 && incoming.price !== existing.price) {
    merged.price = incoming.price;
    isChanged = true;
  }
  if (incoming.comparePrice !== undefined && incoming.comparePrice !== existing.comparePrice) {
    merged.comparePrice = incoming.comparePrice;
    isChanged = true;
  }

  // 4. Primary Image
  // If incoming has a real image, update. If incoming is empty or SVG placeholder, keep existing.
  if (incoming.image && incoming.image.trim()) {
    const isSvgPlaceholder = incoming.image.startsWith('data:image/svg+xml');
    const existingIsSvg = existing.image?.startsWith('data:image/svg+xml');
    
    if (!isSvgPlaceholder || existingIsSvg || !existing.image) {
      const sanitizedImage = sanitizeProductImagePath(incoming.image, merged.category);
      if (sanitizedImage !== existing.image) {
        merged.image = sanitizedImage;
        isChanged = true;
      }
    }
  }

  // 5. Additional Images (Union)
  const existingAddl = Array.isArray(existing.additionalImages) ? existing.additionalImages : [];
  const incomingAddl = Array.isArray(incoming.additionalImages) ? incoming.additionalImages : [];
  const combinedAddl = Array.from(new Set([...existingAddl, ...incomingAddl]))
    .filter(Boolean)
    .map(img => sanitizeProductImagePath(img, merged.category));

  if (combinedAddl.length !== existingAddl.length || combinedAddl.some((img, i) => img !== existingAddl[i])) {
    merged.additionalImages = combinedAddl;
    isChanged = true;
  }

  // 6. Badges
  if (incoming.badge !== undefined && incoming.badge !== existing.badge) {
    merged.badge = incoming.badge;
    isChanged = true;
  }

  // 7. Tags Merging (Union of unique tags)
  const existingTags = Array.isArray(existing.tags) ? existing.tags : [];
  const incomingTags = Array.isArray(incoming.tags) ? incoming.tags : [];
  const tagMap = new Map<string, string>(); // lowercase -> display string
  existingTags.forEach(t => { if (t && t.trim()) tagMap.set(t.trim().toLowerCase(), t.trim()); });
  incomingTags.forEach(t => { if (t && t.trim()) tagMap.set(t.trim().toLowerCase(), t.trim()); });
  const mergedTags = Array.from(tagMap.values());

  if (mergedTags.length !== existingTags.length) {
    merged.tags = mergedTags;
    isChanged = true;
  }

  // 8. Custom Tags Merging
  // Primary customTag
  if (incoming.customTag && incoming.customTag.text) {
    merged.customTag = {
      text: incoming.customTag.text.trim().toUpperCase(),
      color: incoming.customTag.color || '#E21B24'
    };
    isChanged = true;
  }

  // customTags array
  const existingCustomTags = Array.isArray(existing.customTags) ? existing.customTags : [];
  const incomingCustomTags = Array.isArray(incoming.customTags) ? incoming.customTags : [];
  const customTagMap = new Map<string, ProductCustomTag>();
  
  existingCustomTags.forEach(ct => {
    if (ct && ct.text) customTagMap.set(ct.text.trim().toUpperCase(), ct);
  });
  incomingCustomTags.forEach(ct => {
    if (ct && ct.text) customTagMap.set(ct.text.trim().toUpperCase(), ct);
  });
  if (merged.customTag) {
    customTagMap.set(merged.customTag.text.toUpperCase(), merged.customTag);
  }

  const mergedCustomTags = Array.from(customTagMap.values());
  if (mergedCustomTags.length > 0) {
    merged.customTags = mergedCustomTags;
    if (!merged.customTag) {
      merged.customTag = mergedCustomTags[0];
    }
  }

  // 9. Variants
  if (Array.isArray(incoming.variants) && incoming.variants.length > 0) {
    merged.variants = incoming.variants;
    isChanged = true;
  }

  // 10. Description & Details
  if (incoming.description && incoming.description.trim() && incoming.description !== existing.description) {
    merged.description = incoming.description.trim();
    isChanged = true;
  }
  if (Array.isArray(incoming.details) && incoming.details.length > 0 && incoming.details !== existing.details) {
    merged.details = incoming.details;
    isChanged = true;
  }

  // 11. Style & Categorization
  if (incoming.urbanStyle && incoming.urbanStyle !== existing.urbanStyle) {
    merged.urbanStyle = incoming.urbanStyle;
    isChanged = true;
  }

  // 12. Offers & Flags
  if (incoming.showOnOffersPage !== undefined && incoming.showOnOffersPage !== existing.showOnOffersPage) {
    merged.showOnOffersPage = incoming.showOnOffersPage;
    isChanged = true;
  }
  if (incoming.offerDiscountText !== undefined && incoming.offerDiscountText !== existing.offerDiscountText) {
    merged.offerDiscountText = incoming.offerDiscountText;
    isChanged = true;
  }
  if (incoming.featured !== undefined && incoming.featured !== existing.featured) {
    merged.featured = incoming.featured;
    isChanged = true;
  }
  if (incoming.inStock !== undefined && incoming.inStock !== existing.inStock) {
    merged.inStock = incoming.inStock;
    isChanged = true;
  }
  if (incoming.unlisted !== undefined && incoming.unlisted !== existing.unlisted) {
    merged.unlisted = incoming.unlisted;
    isChanged = true;
  }

  return { merged, isChanged };
}

/**
 * Performs a rock-solid Smart Merge of incoming products into current store products.
 * Never deletes or drops existing items; resolves matches by ID, Slug, or (Title + Category);
 * intelligently merges fields; and seamlessly appends newly introduced products.
 */
export function executeSmartMerge(
  currentProducts: Product[],
  incomingProducts: Product[]
): SmartMergeResult {
  const result: Product[] = [];
  const addedTitles: string[] = [];
  const updatedTitles: string[] = [];
  let unchangedCount = 0;

  // Track existing products by multiple keys for reliable matching
  const matchedExistingIds = new Set<string>();

  // Helper indices for current products
  const idIndex = new Map<string, Product>();
  const slugIndex = new Map<string, Product>();
  const titleCategoryIndex = new Map<string, Product>();

  currentProducts.forEach(p => {
    if (p.id) idIndex.set(p.id, p);
    if (p.slug && p.slug.trim()) slugIndex.set(p.slug.trim().toLowerCase(), p);
    
    const normTitle = normalizeTitle(p.title);
    const cat = normalizeCategoryFolder(p.category);
    if (normTitle) {
      titleCategoryIndex.set(`${normTitle}:::${cat}`, p);
    }
  });

  // Process each incoming product
  incomingProducts.forEach(incoming => {
    // 1. Find best match in current catalog
    let match: Product | undefined;

    // Match 1: ID
    if (incoming.id && idIndex.has(incoming.id)) {
      match = idIndex.get(incoming.id);
    }

    // Match 2: Slug
    if (!match && incoming.slug && incoming.slug.trim()) {
      const slugKey = incoming.slug.trim().toLowerCase();
      if (slugIndex.has(slugKey)) {
        match = slugIndex.get(slugKey);
      }
    }

    // Match 3: Title + Category
    if (!match && incoming.title && incoming.title.trim()) {
      const titleKey = `${normalizeTitle(incoming.title)}:::${normalizeCategoryFolder(incoming.category)}`;
      if (titleCategoryIndex.has(titleKey)) {
        match = titleCategoryIndex.get(titleKey);
      }
    }

    if (match) {
      // Existing product found -> Smart Field-Level Merge
      matchedExistingIds.add(match.id);
      const { merged, isChanged } = mergeSingleProduct(match, incoming);
      result.push(merged);

      if (isChanged) {
        updatedTitles.push(merged.title);
      } else {
        unchangedCount++;
      }
    } else {
      // Brand new product -> Sanitize & add
      const cat = normalizeCategoryFolder(incoming.category);
      const newProd: Product = {
        id: incoming.id || `prod-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        title: (incoming.title && incoming.title.trim()) || 'Untitled Product',
        slug: (incoming.slug && incoming.slug.trim()) || `product-${Date.now()}`,
        category: cat as any,
        price: typeof incoming.price === 'number' && incoming.price > 0 ? incoming.price : 18,
        comparePrice: incoming.comparePrice,
        image: incoming.image 
          ? sanitizeProductImagePath(incoming.image, cat)
          : `/images/${cat}/product_${Date.now()}.webp`,
        additionalImages: Array.isArray(incoming.additionalImages)
          ? incoming.additionalImages.map(img => sanitizeProductImagePath(img, cat))
          : [],
        badge: incoming.badge,
        customTag: incoming.customTag,
        customTags: Array.isArray(incoming.customTags) ? incoming.customTags : (incoming.customTag ? [incoming.customTag] : []),
        description: incoming.description || '',
        details: Array.isArray(incoming.details) && incoming.details.length > 0 
          ? incoming.details 
          : ['Custom high-definition print', 'Durable fade-resistant materials'],
        variants: Array.isArray(incoming.variants) ? incoming.variants : [],
        featured: Boolean(incoming.featured),
        inStock: incoming.inStock !== false,
        tags: Array.isArray(incoming.tags) ? incoming.tags : [],
        urbanStyle: incoming.urbanStyle || 'Urban Streetwear',
        unlisted: Boolean(incoming.unlisted),
        inRecycleBin: Boolean(incoming.inRecycleBin),
        trashedAt: incoming.trashedAt,
        showOnOffersPage: Boolean(incoming.showOnOffersPage),
        offerDiscountText: incoming.offerDiscountText,
      };

      result.push(newProd);
      addedTitles.push(newProd.title);
    }
  });

  // Preserve any existing products that were not present in the incoming batch!
  currentProducts.forEach(existing => {
    if (!matchedExistingIds.has(existing.id)) {
      result.push(existing);
    }
  });

  return {
    mergedProducts: result,
    addedCount: addedTitles.length,
    updatedCount: updatedTitles.length,
    unchangedCount,
    details: {
      addedTitles,
      updatedTitles
    }
  };
}
